import React, { useState, useEffect } from 'react';
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { FiClock, FiMail, FiPhone } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { business } from "../../config/business";
import { trackCallClick, trackWhatsAppClick } from "../../utils/analytics";

const TopNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [showTopBar, setShowTopBar] = useState(true);
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setShowTopBar(window.scrollY <= 60);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Industries", path: "/industries" },
    { name: "Locations", path: "/locations" },
    { name: "Resources", path: "/resources" },
    { name: "Gallery", path: "/gallery" },
    { name: "Projects", path: "/projects" },
    { name: "Contact", path: "/contact" },

  ];

  const products = [
    { name: "Industrial Sheds", path: "/industrial-sheds" },
    { name: "Fabrication Services", path: "/FabricationServices" },
    { name: "Industrial Structures", path: "/IndustrialStructures" },
    { name: "Prefabricated Buildings", path: "/pre-fab-structure-buildings" },
    { name: "Steel Fabrication", path: "/structure-steel-fabrication" },
    { name: "Roof & Wall Sheeting", path: "/roof-wall-sheeting" },
    { name: "PEB Warehouse", path: "/services/peb-warehouse" },
    { name: "PEB Factory Buildings", path: "/services/peb-factory-buildings" },
    { name: "PEB Erection", path: "/peb-erection" },
    { name: "Design & Engineering", path: "/design-engineering" },
    { name: "PEB Cost Calculator", path: "/peb-cost-calculator" },
  ];

  return (
    <header className="sticky top-0 z-50 shadow-md">
      {/* Signature brand strip — green-to-orange, echoes the logo's underline */}
      <div className="h-[3px] w-full bg-gradient-to-r from-primary via-primary to-secondary" aria-hidden="true" />

      {showTopBar && (
        /* Utility bar — contact details only, no duplicated nav links */
        <div className="hidden sm:block bg-foreground text-white/80 py-2">
          <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-end items-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <FiClock className="text-sm text-primary" aria-hidden="true" />
              <span>{business.hours}</span>
            </div>
            <span className="w-px h-3 bg-white/20" aria-hidden="true" />
            <a href={business.email.href} className="flex items-center gap-2 hover:text-white transition">
              <FiMail className="text-sm text-primary" aria-hidden="true" />
              {business.email.display}
            </a>
            <span className="w-px h-3 bg-white/20" aria-hidden="true" />
            <a href={business.phone.href} onClick={() => trackCallClick('topbar')} className="flex items-center gap-2 font-semibold text-white hover:text-primary transition">
              <FiPhone className="text-sm text-secondary" aria-hidden="true" />
              {business.phone.display}
            </a>
            <span className="w-px h-3 bg-white/20" aria-hidden="true" />
            <a
              href={business.whatsapp.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('topbar')}
              className="flex items-center gap-2 font-semibold text-[#25D366] hover:text-[#20bd5a] transition"
            >
              <FaWhatsapp className="text-sm" aria-hidden="true" />
              WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Main Nav */}
      <nav className="bg-white" role="navigation" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <Link to="/" aria-label="TR Structures Home" className="shrink-0">
            <Logo />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden xl:flex items-center gap-7">
            {navLinks.slice(0, 2).map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-montserrat font-semibold text-sm transition ${
                  isActive(link.path) ? "text-primary" : "text-foreground hover:text-primary"
                }`}
                aria-current={isActive(link.path) ? "page" : undefined}
              >
                {link.name}
              </Link>
            ))}

            {/* Products Dropdown */}
            <div className="relative group">
              <Link
                to="/products"
                className="font-montserrat font-semibold text-sm text-foreground hover:text-primary flex items-center gap-1"
                aria-haspopup="true"
                aria-expanded="false"
                aria-label="View all products and services"
              >
                Products
                <svg className="w-3.5 h-3.5 group-hover:rotate-180 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Link>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white shadow-xl rounded-xl border-t-2 border-primary opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 overflow-hidden">
                {products.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block px-6 py-3 text-sm text-foreground hover:bg-muted hover:text-primary transition"
                    onClick={() => setProductsOpen(false)}
                    aria-label={item.name}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {navLinks.slice(2).map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-montserrat font-semibold text-sm transition ${
                  isActive(link.path) ? "text-primary" : "text-foreground hover:text-primary"
                }`}
                aria-current={isActive(link.path) ? "page" : undefined}
              >
                {link.name}
              </Link>
            ))}

            <Link
              to="/enquiry"
              className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-md font-montserrat font-bold text-sm hover:bg-primary/90 transition"
              aria-label="Get Quote from TR Structures"
            >
              Get Quote
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="xl:hidden text-foreground p-2"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            {isOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div id="mobile-menu" className="xl:hidden border-t py-4 px-6 bg-white">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 font-semibold border-b ${isActive(link.path) ? "text-primary" : "text-foreground"}`}
                onClick={() => setIsOpen(false)}
                aria-current={isActive(link.path) ? "page" : undefined}
              >
                {link.name}
              </Link>
            ))}
            <div className="py-3">
              <button
                onClick={() => setProductsOpen(!productsOpen)}
                className="w-full text-left font-bold text-primary flex justify-between items-center"
                aria-expanded={productsOpen}
                aria-label="Products menu"
              >
                Products
                <svg className={`w-5 h-5 transition ${productsOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {productsOpen && (
                <div className="mt-3 pl-4 space-y-2">
                  {products.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="block py-2 text-gray-700 hover:text-primary"
                      onClick={() => { setIsOpen(false); setProductsOpen(false); }}
                      aria-label={item.name}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-4 pt-4 border-t text-sm text-muted-foreground">
              <a href={business.phone.href} onClick={() => trackCallClick('mobile_menu')} className="flex items-center gap-2">
                <FiPhone className="text-secondary" aria-hidden="true" /> {business.phone.display}
              </a>
              <a href={business.email.href} className="flex items-center gap-2">
                <FiMail className="text-primary" aria-hidden="true" /> {business.email.display}
              </a>
              <a
                href={business.whatsapp.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('mobile_menu')}
                className="flex items-center gap-2 font-semibold text-[#25D366]"
              >
                <FaWhatsapp aria-hidden="true" /> Chat on WhatsApp
              </a>
            </div>
            <div className="flex gap-2 mt-4">
              <Link
                to="/enquiry"
                className="flex-1 text-center bg-primary text-white py-3 rounded-md font-bold"
                onClick={() => setIsOpen(false)}
                aria-label="Get Quote from TR Structures"
              >
                Get Quote
              </Link>
              <a
                href={business.whatsapp.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('mobile_menu_cta')}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-md font-bold"
                aria-label="Chat with TR Structures on WhatsApp"
              >
                <FaWhatsapp aria-hidden="true" /> WhatsApp
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default TopNav;
